import React from 'react';
import {
    AlertTriangle,
    ArrowRight,
    Bell,
    CheckCheck,
    FileText,
    MessageSquare,
    Package,
    ReceiptText,
    Settings
} from 'lucide-react';

const HeaderNotifications = ({
    notificationsRef,
    isNotificationsOpen,
    setIsNotificationsOpen,
    unreadNotificationCount,
    notificationsWithReadState,
    hasUnreadPanelNotifications,
    markAllNotificationsRead,
    markNotificationRead,
    openNotification,
    openNotificationSettings,
    notificationsEnabled = true,
    isDarkMode
}) => (
    <div ref={notificationsRef} className="relative shrink-0">
        <button
            type="button"
            onClick={() => setIsNotificationsOpen(prev => !prev)}
            className={`relative flex h-9 w-9 items-center justify-center rounded-full border transition-all ${isNotificationsOpen
                ? 'border-[#E55C1A] bg-[#E55C1A] text-white shadow-sm'
                : isDarkMode
                    ? 'border-[#404040] bg-[#292929] text-slate-200 hover:bg-[#404040]'
                    : 'border-[#d8e0ea] bg-white text-[#172033] shadow-sm hover:bg-[#f1f5f9]'
            }`}
            title="Benachrichtigungen"
            aria-label="Benachrichtigungen öffnen"
            aria-expanded={isNotificationsOpen}
        >
            <Bell className="h-[18px] w-[18px]" strokeWidth={2.3} />
            {unreadNotificationCount > 0 && (
                <span className={`absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-black leading-none text-white ring-2 ${isDarkMode ? 'ring-[#1f1f1f]' : 'ring-white'}`}>
                    {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                </span>
            )}
        </button>

        {isNotificationsOpen && (
            <>
                <button
                    type="button"
                    aria-label="Benachrichtigungsfenster schließen"
                    onClick={() => setIsNotificationsOpen(false)}
                    className="fixed inset-0 z-40 cursor-default bg-slate-950/40 backdrop-blur-[1.5px]"
                />
                <div className={`absolute right-0 top-12 z-[60] w-[520px] max-w-[calc(100vw-32px)] overflow-visible rounded-[20px] border-2 ring-1 ${isDarkMode
                    ? 'border-[#506077] bg-[#111923] text-[#E0E0E0] ring-black/50 shadow-[0_34px_100px_rgba(0,0,0,0.72),0_10px_28px_rgba(0,0,0,0.42)]'
                    : 'border-white bg-[#fbfcfe] text-[#172033] ring-slate-300/80 shadow-[0_34px_100px_rgba(15,23,42,0.32),0_10px_28px_rgba(15,23,42,0.18)]'
                }`}>
                    <span className={`pointer-events-none absolute -top-[9px] right-3 h-4 w-4 rotate-45 border-l-2 border-t-2 ${isDarkMode ? 'border-[#506077] bg-[#111923]' : 'border-white bg-[#fbfcfe]'}`} />
                    <div className="overflow-hidden rounded-[18px]">
                <div className={`flex items-center justify-between gap-4 border-b px-5 py-5 ${isDarkMode ? 'border-[#334155] bg-white/[0.025]' : 'border-[#d8e0ea] bg-white'}`}>
                    <div>
                        <h2 className={`text-[22px] font-black tracking-normal ${isDarkMode ? 'text-white' : 'text-[#172033]'}`}>Benachrichtigungen</h2>
                        <p className={`mt-1 text-xs font-semibold ${isDarkMode ? 'text-[#E0E0E0]/60' : 'text-[#536175]'}`}>{unreadNotificationCount ? `${unreadNotificationCount} ungelesen` : 'Alles gelesen'}</p>
                    </div>
                    <button
                        type="button"
                        onClick={markAllNotificationsRead}
                        disabled={!hasUnreadPanelNotifications}
                        className={`inline-flex items-center gap-2 text-sm font-bold transition-colors disabled:cursor-default ${hasUnreadPanelNotifications
                            ? isDarkMode ? 'text-[#E55C1A] hover:text-[#ff8d4a]' : 'text-[#c2410c] hover:text-[#9a3412]'
                            : isDarkMode ? 'text-[#E0E0E0]/45' : 'text-[#536175]/55'
                        }`}
                        aria-label="Alle Benachrichtigungen als gelesen markieren"
                    >
                        <CheckCheck className="h-5 w-5" strokeWidth={2.4} />
                        <span>{hasUnreadPanelNotifications ? 'Alle als gelesen' : 'Alles gelesen'}</span>
                    </button>
                </div>

                <div className="max-h-[470px] overflow-y-auto">
                    {notificationsWithReadState.length ? (
                        <div className="px-5 py-3">
                            {notificationsWithReadState.map((alert, index) => {
                                const critical = alert.severity === 'critical';
                                const important = alert.severity === 'important';
                                const Icon = critical || important
                                    ? AlertTriangle
                                    : alert.area === 'Lager'
                                        ? Package
                                        : alert.area === 'Retouren'
                                            ? MessageSquare
                                            : index % 2
                                                ? ReceiptText
                                                : FileText;
                                const priorityLabel = critical ? 'Kritisch' : important ? 'Wichtig' : alert.area;

                                return (
                                    <div
                                        key={alert.readKey}
                                        className={`group flex w-full items-start gap-4 rounded-xl px-2 py-3 text-left transition-colors ${isDarkMode ? 'hover:bg-[#1A1A1A]' : 'hover:bg-[#fff1e8]/60'}`}
                                    >
                                        <span className={`relative mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border shadow-[0_2px_10px_rgba(15,23,42,0.06)] ${isDarkMode ? 'border-[#404040] bg-[#1A1A1A] text-[#E0E0E0]' : 'border-[#d8e0ea] bg-white text-[#334155]'}`}>
                                            <Icon className={`h-5 w-5 ${critical ? 'text-red-400' : important ? 'text-[#E55C1A]' : isDarkMode ? 'text-[#E0E0E0]' : 'text-[#334155]'}`} strokeWidth={2.2} />
                                            {!alert.isRead && (
                                                <span className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-[#E55C1A] ring-2 ${isDarkMode ? 'ring-[#292929]' : 'ring-[#fbfcfe]'}`} />
                                            )}
                                        </span>
                                        <span className="min-w-0 flex-1 pt-0.5">
                                            <span className={`block text-[15px] font-bold leading-snug ${isDarkMode ? 'text-white' : 'text-[#172033]'}`}>{alert.title}</span>
                                            <span className={`mt-1 block text-sm font-medium leading-snug ${isDarkMode ? 'text-[#E0E0E0]/70' : 'text-[#536175]'}`}>
                                                {alert.isRead ? 'Gelesen' : alert.isRecurring ? 'Erneut' : 'Neu'} · {priorityLabel}
                                            </span>
                                            {alert.detail && (
                                                <span className={`mt-1 line-clamp-2 block text-xs font-medium leading-snug ${isDarkMode ? 'text-[#E0E0E0]/55' : 'text-[#536175]/80'}`}>
                                                    {alert.detail}
                                                </span>
                                            )}
                                        </span>
                                        <span className="mt-1 flex shrink-0 flex-col items-stretch gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => openNotification(alert)}
                                                className={'inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-black transition-colors ' + (isDarkMode
                                                    ? 'border-[#E55C1A]/40 text-[#ff8d4a] hover:bg-[#E55C1A]/10'
                                                    : 'border-orange-200 text-[#c2410c] hover:bg-orange-50'
                                                )}
                                            >
                                                Öffnen
                                                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => markNotificationRead(alert.readKey)}
                                                disabled={alert.isRead}
                                                className={'rounded-lg border px-2.5 py-1.5 text-[11px] font-black transition-colors disabled:cursor-default ' + (alert.isRead
                                                    ? isDarkMode ? 'border-[#404040] text-[#E0E0E0]/45' : 'border-[#d8e0ea] text-[#536175]/55'
                                                    : isDarkMode ? 'border-[#404040] text-[#E0E0E0]/70 hover:bg-white/5' : 'border-[#d8e0ea] text-[#536175] hover:bg-slate-50'
                                                )}
                                            >
                                                {alert.isRead ? 'Gelesen' : 'Als gelesen'}
                                            </button>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex min-h-[360px] flex-col items-center justify-center px-8 text-center">
                            <span className={`flex h-20 w-20 items-center justify-center rounded-full shadow-[inset_0_10px_24px_rgba(15,23,42,0.04)] ${isDarkMode ? 'bg-[#1A1A1A] text-[#E55C1A]' : 'bg-[#fff1e8] text-[#c2410c]'}`}>
                                <Bell className="h-8 w-8 fill-[#E55C1A]/20" strokeWidth={2.2} />
                            </span>
                            <p className={`mt-7 text-xl font-black ${isDarkMode ? 'text-white' : 'text-[#172033]'}`}>{notificationsEnabled ? 'Alles aktuell' : 'Benachrichtigungen pausiert'}</p>
                            <p className={`mt-3 text-base font-medium ${isDarkMode ? 'text-[#E0E0E0]/70' : 'text-[#536175]'}`}>{notificationsEnabled ? 'Neue Hinweise erscheinen hier, sobald etwas wichtig wird.' : 'Aktiviere mindestens ein Thema in den Einstellungen.'}</p>
                        </div>
                    )}
                </div>

                <div className={`flex gap-2 border-t p-4 ${isDarkMode ? 'border-[#404040]' : 'border-[#d8e0ea]'}`}>
                    <button
                        type="button"
                        onClick={openNotificationSettings}
                        className={`inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border text-sm font-bold transition-colors ${isDarkMode ? 'border-[#E55C1A]/40 text-[#ff8d4a] hover:bg-[#E55C1A]/10' : 'border-orange-200 text-[#c2410c] hover:bg-orange-50'}`}
                    >
                        <Settings className="h-4 w-4" />
                        Einstellungen
                    </button>
                    <button
                        type="button"
                        onClick={() => setIsNotificationsOpen(false)}
                        className={`h-10 flex-1 rounded-xl border text-sm font-bold transition-colors ${isDarkMode ? 'border-[#404040] bg-[#1A1A1A] text-white hover:border-[#E55C1A]' : 'border-[#d8e0ea] bg-white text-[#172033] hover:border-[#c2410c]'}`}
                    >
                        Schließen
                    </button>
                </div>
                    </div>
                </div>
            </>
        )}
    </div>
);

export default HeaderNotifications;
